/**
 * CSV Export Utility Functions
 * Provides reusable functions for exporting data to CSV format
 */

/**
 * Downloads a CSV file with the provided data
 * @param {string} csvString - The CSV content as a string
 * @param {string} filename - The filename for the downloaded file
 * @param {string} successMessage - Optional success message to display
 */
export const downloadCSV = (csvString, filename, successMessage = null) => {
  try {
    // Add BOM (Byte Order Mark) for proper UTF-8 encoding with Mandarin characters
    const BOM = '\uFEFF';
    const csvStringWithBOM = BOM + csvString;

    // Create and download the CSV file
    const blob = new Blob([csvStringWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Return success status
      return { success: true, filename, message: successMessage || `CSV export completed successfully. File: ${filename}` };
    } else {
      throw new Error('Download not supported');
    }
  } catch (error) {
    console.error("CSV download error:", error);
    return { success: false, error: "Failed to download CSV file" };
  }
};

/**
 * Generates CSV string from data array and headers
 * @param {Array} headers - Array of header strings
 * @param {Array} data - Array of data rows (each row should be an array)
 * @returns {string} CSV formatted string
 */
export const generateCSVString = (headers, data) => {
  try {
    // Add headers to the beginning
    const csvContent = [headers, ...data];

    // Convert to CSV string with proper escaping
    const csvString = csvContent.map(row =>
      row.map(cell => {
        // Handle null/undefined values
        const cellValue = cell === null || cell === undefined ? "" : String(cell);
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
          return `"${cellValue.replace(/"/g, '""')}"`;
        }
        return cellValue;
      }).join(',')
    ).join('\n');

    return csvString;
  } catch (error) {
    console.error("CSV generation error:", error);
    throw new Error("Failed to generate CSV string");
  }
};

/**
 * Generates a timestamped filename
 * @param {string} baseName - Base name for the file
 * @param {string} extension - File extension (default: 'csv')
 * @returns {string} Timestamped filename
 */
export const generateTimestampedFilename = (baseName, extension = 'csv') => {
  // Get current date in UTC+8 timezone (Asia/Kuala_Lumpur)
  const now = new Date();
  const utc8Time = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // Add 8 hours for UTC+8

  // Format as yyyy-MM-dd_HH:mm:ss
  const year = utc8Time.getUTCFullYear();
  const month = String(utc8Time.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utc8Time.getUTCDate()).padStart(2, '0');
  const hours = String(utc8Time.getUTCHours()).padStart(2, '0');
  const minutes = String(utc8Time.getUTCMinutes()).padStart(2, '0');
  const seconds = String(utc8Time.getUTCSeconds()).padStart(2, '0');

  const timestamp = `${year}-${month}-${day}_${hours}:${minutes}:${seconds}`;
  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Enhanced CSV export function with column formatting support
 * @param {Array} columnConfig - Array of column configuration objects
 * @param {Array} data - Array of data rows
 * @param {string} baseFilename - Base filename without extension
 * @param {string} successMessage - Optional success message
 * @param {boolean} includeAlignmentMetadata - Whether to include alignment metadata in CSV (default: true)
 * @returns {Object} Result object with success status and message
 * 
 * Column configuration format:
 * {
 *   header: string,           // Column header text
 *   key: string,             // Data key to extract from each row object
 *   format: 'text' | 'number' | 'float' | 'general', // Column format (default: 'general')
 *   formatter: function,     // Optional custom formatter function
 *   align: 'left' | 'center' | 'right' // Optional alignment (default: based on format)
 * }
 */
export const exportToCSVWithFormatting = (columnConfig, data, baseFilename, successMessage = null) => {
  try {
    if (!data || data.length === 0) {
      return { success: false, error: "No data to export" };
    }

    // Helper function to format numbers with thousand separators
    const formatNumberWithSeparators = (value) => {
      const num = Number(value);
      if (isNaN(num)) return String(value);

      // Format with thousand separators and decimal points
      return `="${num.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}"`;
    };

    // Helper function to format floats with thousand separators
    const formatFloatWithSeparators = (value) => {
      const num = Number(value);
      if (isNaN(num)) return String(value);

      // Format with thousand separators and always show decimal points
      return `="${num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}"`;
    };

    // Extract headers from column config with alignment metadata
    const headers = columnConfig.map(col => ({
      text: col.header,
      align: col.align || 'center' // Default header alignment to center
    }));

    // Transform data based on column configuration
    const formattedData = data.map((row, rowIndex) =>
      columnConfig.map(col => {
        const value = row[col.key] != null && row[col.key] !== "null" ? row[col.key] : '';

        // Apply custom formatter if provided, then apply format type
        let formattedValue = value;

        if (col.formatter && typeof col.formatter === 'function') {
          formattedValue = col.formatter(value, row, rowIndex);
        }

        // Always apply format-specific formatting after formatter
        switch (col.format) {
          case 'number':
            // Format as number with thousand separators
            if (typeof formattedValue === 'string' && /^0+/.test(formattedValue)) {
              return `="${formattedValue}"`; // Force text format in Excel
            }
            const numValue = Number(formattedValue);
            if (isNaN(numValue)) {
              return `="${String(formattedValue)}"`;
            }
            return formatNumberWithSeparators(numValue);

          case 'float':
            // Format as float with thousand separators and decimal points
            const floatValue = Number(formattedValue);
            if (isNaN(floatValue)) {
              return `="${String(formattedValue)}"`;
            }
            return formatFloatWithSeparators(floatValue);

          case 'text':
            // Force text format, especially useful for IDs with leading zeros
            // Use tab character to prevent Excel from auto-detecting as date/number
            return `="${String(formattedValue)}"`;

          case 'general':
          default:
            // General format - preserve original value
            return String(formattedValue);
        }
      })
    );

    // Use regular CSV generation without alignment metadata
    const headerTexts = headers.map(header => header.text);
    const csvString = generateCSVString(headerTexts, formattedData);
    const filename = generateTimestampedFilename(baseFilename);
    const result = downloadCSV(csvString, filename, successMessage);

    return result;
  } catch (error) {
    console.error("CSV export with formatting error:", error);
    return { success: false, error: "Failed to generate CSV file" };
  }
};

/**
 * Multi-sheet CSV export function
 * Creates a single CSV file with multiple sheets separated by clear dividers
 * @param {Array} sheetsConfig - Array of sheet configuration objects
 * @param {string} baseFilename - Base filename without extension
 * @param {string} successMessage - Optional success message
 * @returns {Object} Result object with success status and message
 * 
 * Sheet configuration format:
 * {
 *   sheetName: string,       // Name of the sheet
 *   columnConfig: Array,     // Column configuration (same as exportToCSVWithFormatting)
 *   data: Array,            // Data rows for this sheet
 * }
 */
export const exportMultiSheetCSV = async (sheetsConfig, baseFilename, successMessage = null) => {
  try {
    if (!sheetsConfig || sheetsConfig.length === 0) {
      return { success: false, error: "No sheets configuration provided" };
    }

    // Check if JSZip is available (would need to be installed)
    // For now, we'll create separate CSV files with sheet names
    const results = [];
    
    for (const sheet of sheetsConfig) {
      const { sheetName, columnConfig, data } = sheet;
      
      if (!data || data.length === 0) {
        console.warn(`No data for sheet: ${sheetName}`);
        continue;
      }

      // Create filename with sheet name
      const sheetFilename = `${baseFilename}_${sheetName}`;
      
      // Export this sheet as CSV
      const result = exportToCSVWithFormatting(
        columnConfig, 
        data, 
        sheetFilename, 
        null // Don't show success message for individual sheets
      );
      
      if (result.success) {
        results.push({ sheetName, filename: result.filename });
      } else {
        console.error(`Failed to export sheet ${sheetName}:`, result.error);
      }
    }

    if (results.length === 0) {
      return { success: false, error: "No sheets were successfully exported" };
    }

    const message = successMessage || `Multi-sheet CSV export completed. ${results.length} files created: ${results.map(r => r.filename).join(', ')}`;
    
    return { 
      success: true, 
      message,
      files: results
    };
    
  } catch (error) {
    console.error("Multi-sheet CSV export error:", error);
    return { success: false, error: "Failed to generate multi-sheet CSV files" };
  }
};

/**
 * Process data for CSV export with formatting
 * @param {Array} columnConfig - Column configuration
 * @param {Array} data - Data to process
 * @returns {Array} Processed data rows
 */
const processDataForCSV = (columnConfig, data) => {
  // Helper function to format numbers with thousand separators
  const formatNumberWithSeparators = (value) => {
    const num = Number(value);
    if (isNaN(num)) return String(value);

    return `="${num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}"`;
  };

  // Helper function to format floats with thousand separators
  const formatFloatWithSeparators = (value) => {
    const num = Number(value);
    if (isNaN(num)) return String(value);

    return `="${num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}"`;
  };

  // Transform data based on column configuration
  return data.map((row, rowIndex) =>
    columnConfig.map(col => {
      const value = row[col.key] != null && row[col.key] !== "null" ? row[col.key] : '';

      // Apply custom formatter if provided, then apply format type
      let formattedValue = value;

      if (col.formatter && typeof col.formatter === 'function') {
        formattedValue = col.formatter(value, row, rowIndex);
      }

      // Always apply format-specific formatting after formatter
      switch (col.format) {
        case 'number':
          // Format as number with thousand separators
          if (typeof formattedValue === 'string' && /^0+/.test(formattedValue)) {
            return `="${formattedValue}"`; // Force text format in Excel
          }
          const numValue = Number(formattedValue);
          if (isNaN(numValue)) {
            return `="${String(formattedValue)}"`;
          }
          return formatNumberWithSeparators(numValue);

        case 'float':
          // Format as float with thousand separators and decimal points
          const floatValue = Number(formattedValue);
          if (isNaN(floatValue)) {
            return `="${String(formattedValue)}"`;
          }
          return formatFloatWithSeparators(floatValue);

        case 'text':
          // Force text format, especially useful for IDs with leading zeros
          return `="${String(formattedValue)}"`;

        case 'general':
        default:
          // General format - preserve original value
          const cellValue = String(formattedValue);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
            return `"${cellValue.replace(/"/g, '""')}"`;
          }
          return cellValue;
      }
    })
  );
};

/**
 * Flattens nested array data for CSV export
 * @param {Array} mainData - Main data array
 * @param {string} nestedKey - Key containing the nested array
 * @param {string} parentIdKey - Key to use as parent identifier
 * @returns {Array} Flattened data with parent information
 */
export const flattenNestedDataForCSV = (mainData, nestedKey, parentIdKey = 'SalesID') => {
  const flattenedData = [];
  let globalIndex = 1; // Global index that continues across all parent records

  mainData.forEach((mainRecord) => {
    const nestedArray = mainRecord[nestedKey] || [];

    // Only include records that actually have nested data
    if (nestedArray.length > 0) {
      // Create a record for each nested item
      nestedArray.forEach((nestedRecord) => {
        flattenedData.push({
          ...nestedRecord,
          [parentIdKey]: mainRecord[parentIdKey],
          ParentSalesID: mainRecord[parentIdKey],
          index: globalIndex++
        });
      });
    }
  });

  return flattenedData;
};